import React, { useEffect, useRef, useState } from 'react';
import { FaStar, FaQuoteRight } from 'react-icons/fa';

const REVIEWS = [
  {
    name: 'Emily Daniel',
    role: 'Performance Marketing Lead',
    company: 'GrowthLabs Agency',
    rating: 5,
    text: 'After switching to ONEBOTADS, we saw a huge increase in engagement. The seamless multi-platform integration made managing our ads completely effortless.',
    avatar: 'ED',
    color: '#221C51',
    bg: '#F5F7FF',
    border: '#E0E4F0',
  },
  {
    name: 'Marcus Rivera',
    role: 'Head of Digital',
    company: 'Nexus eCommerce',
    rating: 5,
    text: 'ONEBOTADS took us from 4 hours per campaign down to under 30 minutes. The AI copy generator alone is worth it — the multi-platform launch sealed the deal.',
    avatar: 'MR',
    color: '#221C51',
    featured: true,
  },
  {
    name: 'Alex Mien',
    role: 'Media Buyer',
    company: 'ScaleUp Media',
    rating: 5,
    text: 'With ONEBOTADS, our team spends significantly less time on ad creation. We focus on strategy now and the results are measurably better across every platform.',
    avatar: 'AM',
    color: '#059669',
    bg: '#ECFDF5',
    border: '#A7F3D0',
  },
];

function RatingBadge({ platform, score, count, color, bg, border }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 18px', borderRadius: 12,
      background: bg, border: `1px solid ${border}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      <div style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1 }}>{score}</div>
      <div>
        <div style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
          {[1,2,3,4,5].map(i => <FaStar key={i} style={{ fontSize: 9, color: '#F59E0B' }} />)}
        </div>
        <div style={{ fontSize: 10, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{count} reviews · {platform}</div>
      </div>
    </div>
  );
}

const Testimonials = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" ref={ref} style={{ background: '#fff', padding: '96px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <div className="mono-label" style={{ color: '#737373', marginBottom: 22 }}>
            <span style={{ color: '#4F46E5' }}>/</span> Testimonials
          </div>
          <h2 style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 700, color: '#111', letterSpacing: '-0.035em', marginBottom: 12, lineHeight: 1.1 }}>
            Trusted by media buyers{' '}
            <em className="serif-accent">worldwide.</em>
          </h2>
          <p style={{ fontSize: 15, color: '#737373', maxWidth: 440, margin: '0 0 32px', lineHeight: 1.65 }}>
            See what marketing teams say after switching to ONEBOTADS.
          </p>

          {/* Review platform badges */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap',
            opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(12px)', transition: 'all 0.5s ease' }}>
            <RatingBadge platform="G2" score="4.9" count="148" color="#111" bg="#F6F7FB" border="#E5E6EF" />
            <RatingBadge platform="Capterra" score="4.8" count="96"  color="#111" bg="#F6F7FB" border="#E5E6EF" />
            <RatingBadge platform="Product Hunt" score="4.9" count="212" color="#111" bg="#F6F7FB" border="#E5E6EF" />
          </div>
        </div>

        {/* Review cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px,1fr))', gap: 20, alignItems: 'start' }}>
          {REVIEWS.map((r, i) => (
            <div key={i} style={{
              padding: '30px 28px',
              borderRadius: 16,
              background: r.featured ? '#0F0B2D' : '#F6F7FB',
              border: r.featured ? 'none' : '1px solid #E5E6EF',
              boxShadow: r.featured ? '0 20px 56px rgba(0,0,0,0.25)' : 'none',
              opacity: visible ? 1 : 0,
              transform: visible ? 'none' : 'translateY(28px)',
              transition: `all 0.6s ${i * 120}ms ease`,
              position: 'relative',
            }}>
              {r.featured && (
                <div className="mono-label" style={{
                  position: 'absolute', top: -11, left: 28,
                  background: '#818CF8',
                  color: '#0F0B2D', fontSize: 9, fontWeight: 700,
                  padding: '4px 12px', borderRadius: 999,
                  whiteSpace: 'nowrap',
                }}>
                  Featured
                </div>
              )}

              <FaQuoteRight style={{ fontSize: 18, color: r.featured ? 'rgba(255,255,255,0.2)' : '#CDCFE0', marginBottom: 16 }} />

              <p style={{ fontSize: 14.5, color: r.featured ? 'rgba(255,255,255,0.8)' : '#404040', lineHeight: 1.75, marginBottom: 24 }}>{r.text}</p>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%',
                  background: r.featured ? 'rgba(255,255,255,0.1)' : '#fff',
                  border: r.featured ? '1px solid rgba(255,255,255,0.2)' : '1px solid #E5E6EF',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: r.featured ? '#fff' : '#111', flexShrink: 0,
                  fontFamily: "'Space Grotesk',sans-serif",
                }}>
                  {r.avatar}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: r.featured ? '#fff' : '#111' }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: r.featured ? 'rgba(255,255,255,0.4)' : '#A3A3A3' }}>{r.role} · {r.company}</div>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <FaStar key={j} style={{ color: r.featured ? '#818CF8' : '#111', fontSize: 10 }} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom social proof bar */}
        <div style={{
          marginTop: 40, padding: '18px 28px',
          background: '#F6F7FB',
          border: '1px solid #E5E6EF',
          borderRadius: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, flexWrap: 'wrap',
          opacity: visible ? 1 : 0, transition: 'opacity 0.8s 0.4s ease',
        }}>
          <div style={{ display: 'flex', marginRight: 4 }}>
            {['ED','MR','AM','JK','SL'].map((initials, i) => (
              <div key={i} style={{ width: 28, height: 28, borderRadius: '50%', background: '#fff', border: '1.5px solid #CDCFE0', marginLeft: i === 0 ? 0 : -8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#404040' }}>
                {initials}
              </div>
            ))}
          </div>
          <span style={{ fontSize: 13, color: '#737373' }}>
            Join <strong style={{ color: '#111' }}>200+ marketing teams</strong> already using ONEBOTADS
          </span>
          <span style={{ fontSize: 12, color: '#A3A3A3', marginLeft: 4 }}>· Avg. rating 4.9/5</span>
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
